/**
 * SFrame155 — Real Estate & Vehicles (secure-inventory si-4)
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Pencil, Trash2, Check, X } from 'lucide-react';
import { SettlementStepPage } from '../../settlement';
import { SETTLEMENT_PHASE_CONFIGS } from '../../../utils/settlementFlowConfig';
import { useSettlementSession } from '../../../hooks/useSettlementSession';
import { useSettlement } from '../../../contexts/SettlementContext';
import { deleteSettlementDocument, uploadSettlementDocument } from '../../../utils/settlementDocuments';

const step = SETTLEMENT_PHASE_CONFIGS['secure-inventory'].steps.find((s) => s.id === 'si-4')!;
const PHASE = 'secure-inventory';
const STEP_ID = step.id;

function rid() {
  return `r-${Math.random().toString(36).slice(2, 11)}`;
}

// ── Types ──────────────────────────────────────────────────────────────────────

type RealEstateType = 'Primary Residence' | 'Rental Property' | 'Land' | 'Other Property';
type VehicleType = 'Car' | 'Boat' | 'RV / Motorcycle' | 'Other';
type ValuationMethod = 'Estimated' | 'Market Value' | 'Appraised' | 'Unknown';
type AppraisalStatus = 'Not Started' | 'In Progress' | 'Completed';
type AccessStatus = 'Has access' | 'No access' | 'Unknown';
type IntendedAction = 'Retain' | 'Transfer' | 'Sell' | 'Unknown';
type ExecutorProgress = 'Not Started' | 'In Progress' | 'Completed' | 'Blocked';

interface DocMeta {
  id: string;
  name: string;
}

interface RealEstateItem {
  id: string;
  type: RealEstateType;
  address: string;
  ownershipType: string;
  mortgageStatus: 'Yes' | 'No' | '';
  estimatedValue: string;
  valuationMethod: ValuationMethod;
  needsAppraisal: boolean;
  appraisalStatus: AppraisalStatus;
  accessStatus: AccessStatus;
  intendedAction: IntendedAction;
  intent: 'sell' | '';
  executorProgressStatus: ExecutorProgress;
  notes: string;
  docMeta: DocMeta[];
}

interface VehicleItem {
  id: string;
  type: VehicleType;
  year: string;
  make: string;
  model: string;
  vin: string;
  description: string;
  titleStatus: 'Clear' | 'Lien' | 'Unknown';
  loanLienStatus: string;
  insuranceStatus: string;
  estimatedValue: string;
  valuationMethod: ValuationMethod;
  needsAppraisal: boolean;
  appraisalStatus: AppraisalStatus;
  accessStatus: AccessStatus;
  intendedAction: IntendedAction;
  intent: 'sell' | '';
  executorProgressStatus: ExecutorProgress;
  notes: string;
  docMeta: DocMeta[];
}

type PropFormState = {
  type: RealEstateType;
  address: string;
  ownershipType: string;
  mortgageStatus: 'Yes' | 'No' | '';
  estimatedValue: string;
  valuationMethod: ValuationMethod;
  needsAppraisal: boolean;
  appraisalStatus: AppraisalStatus;
  accessStatus: AccessStatus;
  intendedAction: IntendedAction;
  executorProgressStatus: ExecutorProgress;
  notes: string;
};

type VehFormState = {
  type: VehicleType;
  year: string;
  make: string;
  model: string;
  vin: string;
  titleStatus: 'Clear' | 'Lien' | 'Unknown';
  loanLienStatus: string;
  insuranceStatus: string;
  estimatedValue: string;
  valuationMethod: ValuationMethod;
  needsAppraisal: boolean;
  appraisalStatus: AppraisalStatus;
  accessStatus: AccessStatus;
  intendedAction: IntendedAction;
  executorProgressStatus: ExecutorProgress;
  notes: string;
};

// ── Defaults ───────────────────────────────────────────────────────────────────

const SETTLEMENT_FIELD_DEFAULTS = {
  estimatedValue: '',
  valuationMethod: 'Estimated' as ValuationMethod,
  needsAppraisal: false,
  appraisalStatus: 'Not Started' as AppraisalStatus,
  accessStatus: 'Unknown' as AccessStatus,
  intendedAction: 'Unknown' as IntendedAction,
  executorProgressStatus: 'Not Started' as ExecutorProgress,
};

const defaultPropForm = (type: RealEstateType = 'Other Property'): PropFormState => ({
  type,
  address: '',
  ownershipType: 'Sole',
  mortgageStatus: '',
  notes: '',
  ...SETTLEMENT_FIELD_DEFAULTS,
});

const defaultVehForm = (type: VehicleType = 'Car'): VehFormState => ({
  type,
  year: '',
  make: '',
  model: '',
  vin: '',
  titleStatus: 'Unknown',
  loanLienStatus: '',
  insuranceStatus: '',
  notes: '',
  ...SETTLEMENT_FIELD_DEFAULTS,
});

// ── Migration helpers ──────────────────────────────────────────────────────────

function mapOldPropertyType(cat: string): RealEstateType {
  if (cat === 'Primary') return 'Primary Residence';
  if (cat === 'Rental') return 'Rental Property';
  if (cat === 'Land') return 'Land';
  return 'Other Property';
}

function mapOldVehicleType(cat: string): VehicleType {
  if (cat === 'Car') return 'Car';
  if (cat === 'Boat') return 'Boat';
  if (cat === 'RV/Motorcycle' || cat === 'RV / Motorcycle') return 'RV / Motorcycle';
  return 'Other';
}

function migratePropertyCard(p: Record<string, unknown>): RealEstateItem {
  return {
    id: String(p.id || rid()),
    type: mapOldPropertyType(String(p.category || '')),
    address: String(p.streetAddress || p.address || ''),
    ownershipType: String(p.ownershipType || 'Sole'),
    mortgageStatus: p.mortgageStatus === 'Yes' ? 'Yes' : p.mortgageStatus === 'No' ? 'No' : '',
    notes: String(p.notes || ''),
    ...SETTLEMENT_FIELD_DEFAULTS,
    estimatedValue: String(p.estimatedValue || ''),
    intent: '',
    docMeta: Array.isArray(p.docMeta) ? (p.docMeta as DocMeta[]) : [],
  };
}

function migrateVehicleCard(v: Record<string, unknown>): VehicleItem {
  const year = String(v.year || '');
  const make = String(v.make || '');
  const model = String(v.model || '');

  return {
    id: String(v.id || rid()),
    type: mapOldVehicleType(String(v.category || '')),
    year,
    make,
    model,
    vin: String(v.vin || ''),
    description: [year, make, model].filter(Boolean).join(' ') || 'Vehicle',
    titleStatus: v.titleStatus === 'Clear' ? 'Clear' : v.titleStatus === 'Lien' ? 'Lien' : 'Unknown',
    loanLienStatus: String(v.loanLienStatus || ''),
    insuranceStatus: String(v.insuranceStatus || ''),
    notes: String(v.notes || ''),
    ...SETTLEMENT_FIELD_DEFAULTS,
    intent: '',
    docMeta: Array.isArray(v.docMeta) ? (v.docMeta as DocMeta[]) : [],
  };
}

// ── Style helpers ──────────────────────────────────────────────────────────────

const inp = 'mt-1 w-full rounded-md border border-stone-200 px-2 py-1.5 text-sm text-stone-800 bg-white';

function progressBadgeCls(status: ExecutorProgress) {
  const base = 'inline-flex text-xs font-semibold px-2 py-0.5 rounded-full border';

  if (status === 'Completed') {
    return `${base} bg-emerald-100 text-emerald-800 border-emerald-200`;
  }

  if (status === 'In Progress') {
    return `${base} bg-blue-100 text-blue-800 border-blue-200`;
  }

  if (status === 'Blocked') {
    return `${base} bg-red-100 text-red-800 border-red-200`;
  }

  return `${base} bg-stone-100 text-stone-700 border-stone-200`;
}

function appraisalBadgeCls(status: AppraisalStatus) {
  const base = 'text-xs font-semibold px-1.5 py-0.5 rounded-full border';
  if (status === 'Completed') return `${base} bg-emerald-100 text-emerald-800 border-emerald-200`;
  if (status === 'In Progress') return `${base} bg-amber-100 text-amber-800 border-amber-200`;
  return `${base} bg-stone-100 text-stone-600 border-stone-200`;
}

function vinSuffix(vin: string) {
  const t = vin.replace(/\s/g, '');
  return t.length <= 6 ? t : t.slice(-6);
}

// ── Tile labels ────────────────────────────────────────────────────────────────

const RE_TILES: RealEstateType[] = ['Primary Residence', 'Rental Property', 'Land', 'Other Property'];
const VEH_TILES: VehicleType[] = ['Car', 'Boat', 'RV / Motorcycle', 'Other'];
// -- ----------------------------------------------------------------------------


type ModalWrapperProps = {
  title: string;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  onSave: () => void | Promise<void>;
  saveDisabled?: boolean;
};

function ModalWrapper({
  title,
  open,
  onClose,
  children,
  onSave,
  saveDisabled,
}: ModalWrapperProps) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-8 sm:items-center"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="relative flex w-full max-w-xl max-h-[calc(100vh-4rem)] flex-col overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="s155-m-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-stone-200 px-5 py-4">
          <h2 id="s155-m-title" className="text-xl font-semibold text-stone-900">
            {title}
          </h2>

          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {children}
        </div>

        <div className="flex shrink-0 justify-end gap-3 border-t border-stone-200 bg-stone-50 px-5 py-4">
          <button
            type="button"
            className="rounded-lg border border-stone-300 bg-white px-5 py-2 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-100"
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            type="button"
            className="rounded-lg bg-cyan-700 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-cyan-800 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={saveDisabled}
            onClick={() => void onSave()}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function SFrame155Property() {
  const { estateId } = useParams<{ estateId: string }>();
  const { isArchived } = useSettlement();
  const { loadFrameData, saveFrameData } = useSettlementSession(estateId ?? null, { readOnly: isArchived });

  const consolidatedStep = {
    ...step,
    title: 'Real Estate & Vehicles',
    description: 'Document properties and vehicles, including ownership, location, and loan status.',
    subTasks: [],
  };

  const [realEstate, setRealEstate] = useState<RealEstateItem[]>([]);
  const [vehicles, setVehicles] = useState<VehicleItem[]>([]);
  const [realDone, setRealDone] = useState(false);
  const [vehDone, setVehDone] = useState(false);
  const [hydrated, setHydrated] = useState(!estateId);

  const [propOpen, setPropOpen] = useState(false);
  const [propEdit, setPropEdit] = useState<string | null>(null);
  const [propForm, setPropForm] = useState<PropFormState>(defaultPropForm());
  const [propFiles, setPropFiles] = useState<File[]>([]);

  const [vehOpen, setVehOpen] = useState(false);
  const [vehEdit, setVehEdit] = useState<string | null>(null);
  const [vehForm, setVehForm] = useState<VehFormState>(defaultVehForm());
  const [vehFiles, setVehFiles] = useState<File[]>([]);

  // ── Load & migrate ───────────────────────────────────────────────────────────

  useEffect(() => {
    if (!estateId) {
      setHydrated(true);
      return;
    }

    let cancelled = false;

    void (async () => {
      const fd = await loadFrameData('sframe-155');
      if (cancelled) return;

      const d = fd?.data as Record<string, unknown> | undefined;
      if (!d) {
        setHydrated(true);
        return;
      }

      let reItems: RealEstateItem[] = [];

      if (Array.isArray(d.realEstate) && (d.realEstate as unknown[]).length > 0) {
        const first = (d.realEstate as Record<string, unknown>[])[0];

        if ('type' in first && 'address' in first) {
          reItems = (d.realEstate as RealEstateItem[]).map((p) => ({
            ...SETTLEMENT_FIELD_DEFAULTS,
            ...p,
            intent: p.intent ?? '',
          }));
        } else {
          reItems = (d.realEstate as Record<string, unknown>[]).map((x) => ({
            id: String(x.id || rid()),
            type: 'Other Property' as RealEstateType,
            address: String(x.address || ''),
            ownershipType: String(x.vesting || ''),
            mortgageStatus: String(x.mortgageLender || '').trim() ? ('Yes' as const) : ('' as const),
            notes: [String(x.vesting || ''), String(x.parcel || '')].filter(Boolean).join(' · '),
            ...SETTLEMENT_FIELD_DEFAULTS,
            estimatedValue: String(x.valueEstimate || ''),
            intent: '' as const,
            docMeta: [],
          }));
        }
      } else if (Array.isArray(d.properties) && (d.properties as unknown[]).length > 0) {
        reItems = (d.properties as Record<string, unknown>[]).map(migratePropertyCard);
      }

      let vehItems: VehicleItem[] = [];

      if (Array.isArray(d.vehicles) && (d.vehicles as unknown[]).length > 0) {
        const first = (d.vehicles as Record<string, unknown>[])[0];

        if ('type' in first && 'description' in first) {
          vehItems = (d.vehicles as VehicleItem[]).map((v) => ({
            ...SETTLEMENT_FIELD_DEFAULTS,
            ...v,
            intent: v.intent ?? '',
          }));
        } else {
          vehItems = (d.vehicles as Record<string, unknown>[]).map((x) => {
            const tokens = String(x.description || '').trim().split(/\s+/).filter(Boolean);
            const yr = tokens[0] && /^\d{4}$/.test(tokens[0]) ? tokens.shift() ?? '' : '';
            const mk = tokens[0] ?? '';
            const md = tokens.slice(1).join(' ');
            const description = [yr, mk, md].filter(Boolean).join(' ') || 'Vehicle';

            return {
              id: String(x.id || rid()),
              type: 'Car' as VehicleType,
              year: yr,
              make: mk,
              model: md,
              vin: String(x.vin || ''),
              description,
              titleStatus: String(x.titleLocation || '').toLowerCase().includes('lien')
                ? ('Lien' as const)
                : ('Unknown' as const),
              loanLienStatus: String(x.lienholder || ''),
              insuranceStatus: '',
              notes: String(x.registration || ''),
              ...SETTLEMENT_FIELD_DEFAULTS,
              intent: '' as const,
              docMeta: [],
            };
          });
        }
      } else if (Array.isArray(d.vehicleCards) && (d.vehicleCards as unknown[]).length > 0) {
        vehItems = (d.vehicleCards as Record<string, unknown>[]).map(migrateVehicleCard);
      }

      if (Array.isArray(d.realWorksheet)) {
        for (const raw of d.realWorksheet as Record<string, unknown>[]) {
          const addr = String(raw.address || '').trim();
          if (!addr) continue;

          const normAddr = addr.toLowerCase();
          if (reItems.some((p) => p.address.toLowerCase() === normAddr)) continue;

          reItems.push({
            id: rid(),
            type: 'Other Property',
            address: addr,
            ownershipType: String(raw.ownership || ''),
            mortgageStatus: String(raw.mortgageBalance || '').trim() ? 'Yes' : '',
            notes: raw.taxApn ? `APN: ${String(raw.taxApn)}` : '',
            ...SETTLEMENT_FIELD_DEFAULTS,
            estimatedValue: String(raw.estimatedValue || ''),
            intent: '',
            docMeta: [],
          });
        }
      }

      if (Array.isArray(d.vehicleWorksheet)) {
        for (const raw of d.vehicleWorksheet as Record<string, unknown>[]) {
          const vin = String(raw.vin || '').trim();
          const ymk = String(raw.ymk || '').trim();

          if (!ymk && !vin) continue;
          if (vin && vehItems.some((v) => v.vin === vin)) continue;

          const tokens = ymk.split(/\s+/).filter(Boolean);
          const year = tokens[0] && /^\d{4}$/.test(tokens[0]) ? tokens.shift() ?? '' : '';
          const make = tokens[0] ?? '';
          const model = tokens.slice(1).join(' ');

          if (!vin && (make || model)) {
            const normKey = [year, make, model].join(' ').toLowerCase().trim();
            if (vehItems.some((v) => [v.year, v.make, v.model].join(' ').toLowerCase().trim() === normKey)) continue;
          }

          vehItems.push({
            id: rid(),
            type: 'Car',
            year,
            make,
            model,
            vin,
            description: [year, make, model].filter(Boolean).join(' ') || 'Vehicle',
            titleStatus: String(raw.titleStatus || '').toLowerCase().includes('lien') ? 'Lien' : 'Unknown',
            loanLienStatus: String(raw.lienholder || ''),
            insuranceStatus: '',
            notes: String(raw.registrationStatus || ''),
            ...SETTLEMENT_FIELD_DEFAULTS,
            intent: '',
            docMeta: [],
          });
        }
      }

      setRealEstate(reItems);
      setVehicles(vehItems);

      if (typeof d.realSectionDone === 'boolean') setRealDone(d.realSectionDone);
      if (typeof d.vehiclesSectionDone === 'boolean') setVehDone(d.vehiclesSectionDone);

      setHydrated(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [estateId, loadFrameData]);

  // ── Auto-save ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!estateId || !hydrated) return;

    const t = window.setTimeout(() => {
      void saveFrameData('sframe-155', {
        realEstate,
        vehicles,
        realSectionDone: realDone,
        vehiclesSectionDone: vehDone,
      });
    }, 650);

    return () => window.clearTimeout(t);
  }, [realEstate, vehicles, realDone, vehDone, estateId, hydrated, saveFrameData]);

  // ── Document helpers ─────────────────────────────────────────────────────────

  const uploadForCard = useCallback(
    async (kind: 'property' | 'vehicle', cardId: string, files: File[]) => {
      if (!estateId || isArchived || !files.length) return [] as DocMeta[];

      const out: DocMeta[] = [];

      for (const file of files) {
        const row = await uploadSettlementDocument({
          estateId,
          phase: PHASE,
          stepId: STEP_ID,
          file,
          description: JSON.stringify({ phase: PHASE, stepId: STEP_ID, kind, cardId }),
        });

        out.push({ id: row.id, name: row.filename });
      }

      return out;
    },
    [estateId, isArchived]
  );

  const removeDoc = useCallback(
    async (docId: string) => {
      if (!estateId || isArchived) return;

      await deleteSettlementDocument(estateId, docId);

      setRealEstate((list) => list.map((c) => ({ ...c, docMeta: c.docMeta.filter((d) => d.id !== docId) })));
      setVehicles((list) => list.map((c) => ({ ...c, docMeta: c.docMeta.filter((d) => d.id !== docId) })));
    },
    [estateId, isArchived]
  );

  // ── Validation ────────────────────────────────────────────────────────────────

  const propSaveDisabled = isArchived || !propForm.estimatedValue.trim() || !propForm.address.trim();

  const vehSaveDisabled =
    isArchived ||
    !vehForm.estimatedValue.trim() ||
    !(vehForm.year.trim() || vehForm.make.trim() || vehForm.model.trim() || vehForm.vin.trim());

  // ── Save handlers ────────────────────────────────────────────────────────────

  async function saveProperty() {
    if (!propForm.estimatedValue.trim() || !propForm.address.trim()) return;

    const id = propEdit ?? rid();
    const add = await uploadForCard('property', id, propFiles);
    const prev = realEstate.find((p) => p.id === id);

    const item: RealEstateItem = {
      id,
      ...propForm,
      intent: propForm.intendedAction === 'Sell' ? 'sell' : '',
      docMeta: [...(prev?.docMeta ?? []), ...add],
    };

    setRealEstate((list) => {
      const i = list.findIndex((p) => p.id === id);
      if (i < 0) return [...list, item];

      const n = [...list];
      n[i] = item;
      return n;
    });

    setPropOpen(false);
    setPropFiles([]);
  }

  async function saveVehicle() {
    if (
      !vehForm.estimatedValue.trim() ||
      !(vehForm.year.trim() || vehForm.make.trim() || vehForm.model.trim() || vehForm.vin.trim())
    ) {
      return;
    }

    const id = vehEdit ?? rid();
    const add = await uploadForCard('vehicle', id, vehFiles);
    const prev = vehicles.find((v) => v.id === id);

    const item: VehicleItem = {
      id,
      ...vehForm,
      description: [vehForm.year, vehForm.make, vehForm.model].filter(Boolean).join(' ') || 'Vehicle',
      intent: vehForm.intendedAction === 'Sell' ? 'sell' : '',
      docMeta: [...(prev?.docMeta ?? []), ...add],
    };

    setVehicles((list) => {
      const i = list.findIndex((v) => v.id === id);
      if (i < 0) return [...list, item];

      const n = [...list];
      n[i] = item;
      return n;
    });

    setVehOpen(false);
    setVehFiles([]);
  }
  
  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <SettlementStepPage
      step={consolidatedStep}
      phase="secure-inventory"
      overviewRoute="/sframe-143"
      hidePlannerBanner
      settlementDocumentContext={{ phase: PHASE, stepId: STEP_ID }}
    >
      <p className="text-sm text-stone-600 mb-4">
        Document properties and vehicles, including ownership, location, and loan status.
      </p>

      <div className="mb-8 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        Some assets may require formal appraisal for tax or distribution purposes.
      </div>

      <section className="mb-10 rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
        <div className="mb-5">
          <h2 className="text-lg font-semibold text-blue-900">Real Estate</h2>
          <p className="mt-1 text-sm text-stone-600">
            Document real property, ownership, mortgage status, estimated value, and appraisal needs.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {RE_TILES.map((type) => (
            <button
              key={type}
              type="button"
              disabled={isArchived}
              onClick={() => {
                setPropForm(defaultPropForm(type));
                setPropEdit(null);
                setPropFiles([]);
                setPropOpen(true);
              }}
              className="px-3 py-2 rounded-lg border border-stone-300 text-sm font-semibold text-stone-700 hover:bg-stone-50 hover:border-stone-400 transition-colors disabled:opacity-50"
            >
              + {type}
            </button>
          ))}
        </div>

        {realEstate.length === 0 ? (
          <p className="text-sm text-stone-400 italic mb-6">No properties added yet. Use the tiles above to add one.</p>
        ) : (
          <div className="space-y-3 mb-6">
            {realEstate.map((p) => (
              <div
                key={p.id}
                className={`rounded-xl border p-4 ${
                  realDone ? 'border-emerald-200 bg-emerald-50/40' : 'border-stone-200 bg-stone-50/50'
                }`}
              >
                <div className="flex justify-between gap-2 mb-3">
                  <div>
                    <p className="text-sm font-semibold text-stone-900">{p.address || 'Property'}</p>
                    <p className="text-xs text-stone-500 mt-0.5">{p.type}</p>
                  </div>

                  <div className="flex gap-1 shrink-0">
                    <button
                      type="button"
                      className="text-stone-400 hover:text-stone-700 p-1 disabled:opacity-40"
                      disabled={isArchived}
                      onClick={() => {
                        setPropEdit(p.id);
                        setPropForm({
                          type: p.type,
                          address: p.address,
                          ownershipType: p.ownershipType,
                          mortgageStatus: p.mortgageStatus,
                          estimatedValue: p.estimatedValue,
                          valuationMethod: p.valuationMethod,
                          needsAppraisal: p.needsAppraisal,
                          appraisalStatus: p.appraisalStatus,
                          accessStatus: p.accessStatus,
                          intendedAction: p.intendedAction,
                          executorProgressStatus: p.executorProgressStatus,
                          notes: p.notes,
                        });
                        setPropFiles([]);
                        setPropOpen(true);
                      }}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      className="text-stone-400 hover:text-red-600 p-1 disabled:opacity-40"
                      disabled={isArchived}
                      onClick={() => setRealEstate((list) => list.filter((x) => x.id !== p.id))}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-xs text-stone-700 mb-3">
                  <div>
                    <dt className="text-stone-400">Ownership</dt>
                    <dd>{p.ownershipType || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-stone-400">Mortgage</dt>
                    <dd>{p.mortgageStatus || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-stone-400">Est. Value</dt>
                    <dd>{p.estimatedValue || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-stone-400">Valuation Method</dt>
                    <dd>{p.valuationMethod}</dd>
                  </div>
                  <div>
                    <dt className="text-stone-400">Appraisal Status</dt>
                    <dd>
                      {p.needsAppraisal ? (
                        <span className={appraisalBadgeCls(p.appraisalStatus)}>{p.appraisalStatus}</span>
                      ) : (
                        <span className="text-stone-400">Not needed</span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-stone-400">Access</dt>
                    <dd>{p.accessStatus}</dd>
                  </div>
                  <div>
                    <dt className="text-stone-400">Intended Action</dt>
                    <dd>{p.intendedAction}</dd>
                  </div>
                </dl>

                <span className={progressBadgeCls(p.executorProgressStatus)}>{p.executorProgressStatus}</span>

                {p.notes ? <p className="mt-2 text-xs text-stone-500 italic">{p.notes}</p> : null}

                {p.docMeta.length > 0 && (
                  <ul className="mt-3 text-xs space-y-1 border-t border-stone-100 pt-2">
                    <li className="font-medium text-stone-500">Attachments</li>
                    {p.docMeta.map((d) => (
                      <li key={d.id} className="flex justify-between gap-2">
                        <span className="truncate">{d.name}</span>
                        {!isArchived && (
                          <button type="button" className="text-red-600 shrink-0 hover:underline" onClick={() => void removeDoc(d.id)}>
                            Remove
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-end pt-2 border-t border-stone-100">
          <button
            type="button"
            disabled={isArchived}
            aria-pressed={realDone}
            onClick={() => setRealDone((done) => !done)}
            className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-60 ${
              realDone
                ? 'border-emerald-200 bg-emerald-100 text-emerald-800 hover:bg-emerald-50'
                : 'border-stone-300 bg-white text-stone-700 hover:border-stone-400 hover:bg-stone-50'
            }`}
          >
            <span>{realDone ? 'Real Estate Complete' : 'Mark Real Estate Complete'}</span>
            {realDone && <Check className="h-4 w-4" aria-hidden="true" />}
          </button>
        </div>
      </section>

      <section className="mb-10 rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
        <div className="mb-5">
          <h2 className="text-lg font-semibold text-blue-900">Vehicles</h2>
          <p className="mt-1 text-sm text-stone-600">
            Document titled vehicles, VIN/title information, loan or lien status, insurance, and transfer readiness.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {VEH_TILES.map((type) => (
            <button
              key={type}
              type="button"
              disabled={isArchived}
              onClick={() => {
                setVehForm(defaultVehForm(type));
                setVehEdit(null);
                setVehFiles([]);
                setVehOpen(true);
              }}
              className="px-3 py-2 rounded-lg border border-stone-300 text-sm font-semibold text-stone-700 hover:bg-stone-50 hover:border-stone-400 transition-colors disabled:opacity-50"
            >
              + {type}
            </button>
          ))}
        </div>

        {vehicles.length === 0 ? (
          <p className="text-sm text-stone-400 italic mb-6">No vehicles added yet. Use the tiles above to add one.</p>
        ) : (
          <div className="space-y-3 mb-6">
            {vehicles.map((v) => (
              <div
                key={v.id}
                className={`rounded-xl border p-4 ${
                  vehDone ? 'border-emerald-200 bg-emerald-50/40' : 'border-stone-200 bg-stone-50/50'
                }`}
              >
                <div className="flex justify-between gap-2 mb-3">
                  <div>
                    <p className="text-sm font-semibold text-stone-900">{v.description || 'Vehicle'}</p>
                    <p className="text-xs text-stone-500 mt-0.5">
                      {v.type}
                      {v.vin ? ` · VIN …${vinSuffix(v.vin)}` : ''}
                    </p>
                  </div>

                  <div className="flex gap-1 shrink-0">
                    <button
                      type="button"
                      className="text-stone-400 hover:text-stone-700 p-1 disabled:opacity-40"
                      disabled={isArchived}
                      onClick={() => {
                        setVehEdit(v.id);
                        setVehForm({
                          type: v.type,
                          year: v.year,
                          make: v.make,
                          model: v.model,
                          vin: v.vin,
                          titleStatus: v.titleStatus,
                          loanLienStatus: v.loanLienStatus,
                          insuranceStatus: v.insuranceStatus,
                          estimatedValue: v.estimatedValue,
                          valuationMethod: v.valuationMethod,
                          needsAppraisal: v.needsAppraisal,
                          appraisalStatus: v.appraisalStatus,
                          accessStatus: v.accessStatus,
                          intendedAction: v.intendedAction,
                          executorProgressStatus: v.executorProgressStatus,
                          notes: v.notes,
                        });
                        setVehFiles([]);
                        setVehOpen(true);
                      }}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      className="text-stone-400 hover:text-red-600 p-1 disabled:opacity-40"
                      disabled={isArchived}
                      onClick={() => setVehicles((list) => list.filter((x) => x.id !== v.id))}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-xs text-stone-700 mb-3">
                  <div>
                    <dt className="text-stone-400">Title</dt>
                    <dd>{v.titleStatus}</dd>
                  </div>
                  <div>
                    <dt className="text-stone-400">Loan / Lien</dt>
                    <dd>{v.loanLienStatus || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-stone-400">Est. Value</dt>
                    <dd>{v.estimatedValue || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-stone-400">Valuation Method</dt>
                    <dd>{v.valuationMethod}</dd>
                  </div>
                  <div>
                    <dt className="text-stone-400">Appraisal Status</dt>
                    <dd>
                      {v.needsAppraisal ? (
                        <span className={appraisalBadgeCls(v.appraisalStatus)}>{v.appraisalStatus}</span>
                      ) : (
                        <span className="text-stone-400">Not needed</span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-stone-400">Access</dt>
                    <dd>{v.accessStatus}</dd>
                  </div>
                  <div>
                    <dt className="text-stone-400">Intended Action</dt>
                    <dd>{v.intendedAction}</dd>
                  </div>
                </dl>

                <span className={progressBadgeCls(v.executorProgressStatus)}>{v.executorProgressStatus}</span>

                {v.insuranceStatus ? <p className="mt-2 text-xs text-stone-500">Insurance: {v.insuranceStatus}</p> : null}
                {v.notes ? <p className="mt-1 text-xs text-stone-500 italic">{v.notes}</p> : null}

                {v.docMeta.length > 0 && (
                  <ul className="mt-3 text-xs space-y-1 border-t border-stone-100 pt-2">
                    <li className="font-medium text-stone-500">Attachments</li>
                    {v.docMeta.map((d) => (
                      <li key={d.id} className="flex justify-between gap-2">
                        <span className="truncate">{d.name}</span>
                        {!isArchived && (
                          <button type="button" className="text-red-600 shrink-0 hover:underline" onClick={() => void removeDoc(d.id)}>
                            Remove
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-end pt-2 border-t border-stone-100">
          <button
            type="button"
            disabled={isArchived}
            aria-pressed={vehDone}
            onClick={() => setVehDone((done) => !done)}
            className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-60 ${
              vehDone
                ? 'border-emerald-200 bg-emerald-100 text-emerald-800 hover:bg-emerald-50'
                : 'border-stone-300 bg-white text-stone-700 hover:border-stone-400 hover:bg-stone-50'
            }`}
          >
            <span>{vehDone ? 'Vehicles Complete' : 'Mark Vehicles Complete'}</span>
            {vehDone && <Check className="h-4 w-4" aria-hidden="true" />}
          </button>
        </div>
      </section>

      <ModalWrapper
        title={propEdit ? 'Edit Property' : 'Add Property'}
        open={propOpen}
        onClose={() => {
          setPropOpen(false);
          setPropFiles([]);
        }}
        onSave={saveProperty}
        saveDisabled={propSaveDisabled}
      >
        <div className="space-y-3">
          <label className="block text-xs font-semibold text-stone-700">
            Type
            <select
              className={inp}
              value={propForm.type}
              onChange={(e) => setPropForm((f) => ({ ...f, type: e.target.value as RealEstateType }))}
            >
              {RE_TILES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-xs font-semibold text-stone-700">
            Property Address <span className="text-red-500">*</span>
            <input
              className={inp}
              placeholder="e.g. 123 Main St, City, State"
              value={propForm.address}
              onChange={(e) => setPropForm((f) => ({ ...f, address: e.target.value }))}
            />
          </label>

          <label className="block text-xs font-semibold text-stone-700">
            Ownership Type
            <select
              className={inp}
              value={propForm.ownershipType}
              onChange={(e) => setPropForm((f) => ({ ...f, ownershipType: e.target.value }))}
            >
              {['Sole', 'Joint', 'Trust', 'Other'].map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-xs font-semibold text-stone-700">
            Mortgage Status
            <select
              className={inp}
              value={propForm.mortgageStatus}
              onChange={(e) => setPropForm((f) => ({ ...f, mortgageStatus: e.target.value as 'Yes' | 'No' | '' }))}
            >
              <option value="">Unknown</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </label>

          <label className="block text-xs font-semibold text-stone-700">
            Estimated Value <span className="text-red-500">*</span>
            <input
              className={inp}
              placeholder="e.g. $450,000"
              value={propForm.estimatedValue}
              onChange={(e) => setPropForm((f) => ({ ...f, estimatedValue: e.target.value }))}
            />
          </label>

          <label className="block text-xs font-semibold text-stone-700">
            Valuation Method
            <select
              className={inp}
              value={propForm.valuationMethod}
              onChange={(e) => setPropForm((f) => ({ ...f, valuationMethod: e.target.value as ValuationMethod }))}
            >
              {(['Estimated', 'Market Value', 'Appraised', 'Unknown'] as ValuationMethod[]).map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>

          <div>
            <p className="text-xs font-semibold text-stone-700 mb-1">Needs Appraisal</p>
            <div className="flex gap-4">
              {([true, false] as const).map((val) => (
                <label key={String(val)} className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="prop-needsAppraisal"
                    checked={propForm.needsAppraisal === val}
                    onChange={() => setPropForm((f) => ({ ...f, needsAppraisal: val }))}
                  />
                  {val ? 'Yes' : 'No'}
                </label>
              ))}
            </div>
          </div>

          {propForm.needsAppraisal && (
            <label className="block text-xs font-semibold text-stone-700">
              Appraisal Status
              <select
                className={inp}
                value={propForm.appraisalStatus}
                onChange={(e) => setPropForm((f) => ({ ...f, appraisalStatus: e.target.value as AppraisalStatus }))}
              >
                {(['Not Started', 'In Progress', 'Completed'] as AppraisalStatus[]).map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="block text-xs font-semibold text-stone-700">
            Access Status
            <select
              className={inp}
              value={propForm.accessStatus}
              onChange={(e) => setPropForm((f) => ({ ...f, accessStatus: e.target.value as AccessStatus }))}
            >
              {(['Has access', 'No access', 'Unknown'] as AccessStatus[]).map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-xs font-semibold text-stone-700">
            Intended Action
            <select
              className={inp}
              value={propForm.intendedAction}
              onChange={(e) => setPropForm((f) => ({ ...f, intendedAction: e.target.value as IntendedAction }))}
            >
              {(['Retain', 'Transfer', 'Sell', 'Unknown'] as IntendedAction[]).map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-xs font-semibold text-stone-700">
            Executor Progress Status
            <select
              className={inp}
              value={propForm.executorProgressStatus}
              onChange={(e) => setPropForm((f) => ({ ...f, executorProgressStatus: e.target.value as ExecutorProgress }))}
            >
              {(['Not Started', 'In Progress', 'Completed', 'Blocked'] as ExecutorProgress[]).map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-xs font-semibold text-stone-700">
            Notes
            <textarea
              className={`${inp} min-h-[72px]`}
              value={propForm.notes}
              onChange={(e) => setPropForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </label>

          <div>
            <p className="text-xs font-semibold text-stone-600 mb-2">Attachments (saved on Save)</p>
            <input
              type="file"
              multiple
              disabled={isArchived}
              className="text-sm"
              onChange={(e) => setPropFiles(Array.from(e.target.files || []))}
            />
            {propFiles.length > 0 && <p className="text-xs text-stone-500 mt-1">{propFiles.length} file(s) selected</p>}
          </div>
        </div>
      </ModalWrapper>

      <ModalWrapper
        title={vehEdit ? 'Edit Vehicle' : 'Add Vehicle'}
        open={vehOpen}
        onClose={() => {
          setVehOpen(false);
          setVehFiles([]);
        }}
        onSave={saveVehicle}
        saveDisabled={vehSaveDisabled}
      >
        <div className="space-y-3">
          <label className="block text-xs font-semibold text-stone-700">
            Type
            <select
              className={inp}
              value={vehForm.type}
              onChange={(e) => setVehForm((f) => ({ ...f, type: e.target.value as VehicleType }))}
            >
              {VEH_TILES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>

          <div>
            <p className="text-xs font-semibold text-stone-700 mb-1">
              Vehicle Identifier <span className="text-red-500">*</span>
            </p>
            <p className="text-xs text-stone-500 mb-1">Enter at least one identifier: year, make, model, or VIN.</p>

            <div className="grid grid-cols-3 gap-2">
              <label className="text-xs font-semibold text-stone-700">
                Year
                <input
                  className={inp}
                  placeholder="2020"
                  value={vehForm.year}
                  onChange={(e) => setVehForm((f) => ({ ...f, year: e.target.value }))}
                />
              </label>

              <label className="text-xs font-semibold text-stone-700">
                Make
                <input
                  className={inp}
                  placeholder="Toyota"
                  value={vehForm.make}
                  onChange={(e) => setVehForm((f) => ({ ...f, make: e.target.value }))}
                />
              </label>

              <label className="text-xs font-semibold text-stone-700">
                Model
                <input
                  className={inp}
                  placeholder="Camry"
                  value={vehForm.model}
                  onChange={(e) => setVehForm((f) => ({ ...f, model: e.target.value }))}
                />
              </label>
            </div>
          </div>

          <label className="block text-xs font-semibold text-stone-700">
            VIN
            <input className={inp} value={vehForm.vin} onChange={(e) => setVehForm((f) => ({ ...f, vin: e.target.value }))} />
          </label>

          <label className="block text-xs font-semibold text-stone-700">
            Title Status
            <select
              className={inp}
              value={vehForm.titleStatus}
              onChange={(e) => setVehForm((f) => ({ ...f, titleStatus: e.target.value as VehicleItem['titleStatus'] }))}
            >
              <option value="Clear">Clear</option>
              <option value="Lien">Lien</option>
              <option value="Unknown">Unknown</option>
            </select>
          </label>

          <label className="block text-xs font-semibold text-stone-700">
            Loan / Lien Status
            <input
              className={inp}
              value={vehForm.loanLienStatus}
              onChange={(e) => setVehForm((f) => ({ ...f, loanLienStatus: e.target.value }))}
            />
          </label>

          <label className="block text-xs font-semibold text-stone-700">
            Insurance Status
            <input
              className={inp}
              value={vehForm.insuranceStatus}
              onChange={(e) => setVehForm((f) => ({ ...f, insuranceStatus: e.target.value }))}
            />
          </label>

          <label className="block text-xs font-semibold text-stone-700">
            Estimated Value <span className="text-red-500">*</span>
            <input
              className={inp}
              placeholder="e.g. $25,000"
              value={vehForm.estimatedValue}
              onChange={(e) => setVehForm((f) => ({ ...f, estimatedValue: e.target.value }))}
            />
          </label>

          <label className="block text-xs font-semibold text-stone-700">
            Valuation Method
            <select
              className={inp}
              value={vehForm.valuationMethod}
              onChange={(e) => setVehForm((f) => ({ ...f, valuationMethod: e.target.value as ValuationMethod }))}
            >
              {(['Estimated', 'Market Value', 'Appraised', 'Unknown'] as ValuationMethod[]).map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>

          <div>
            <p className="text-xs font-semibold text-stone-700 mb-1">Needs Appraisal</p>
            <div className="flex gap-4">
              {([true, false] as const).map((val) => (
                <label key={String(val)} className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="veh-needsAppraisal"
                    checked={vehForm.needsAppraisal === val}
                    onChange={() => setVehForm((f) => ({ ...f, needsAppraisal: val }))}
                  />
                  {val ? 'Yes' : 'No'}
                </label>
              ))}
            </div>
          </div>

          {vehForm.needsAppraisal && (
            <label className="block text-xs font-semibold text-stone-700">
              Appraisal Status
              <select
                className={inp}
                value={vehForm.appraisalStatus}
                onChange={(e) => setVehForm((f) => ({ ...f, appraisalStatus: e.target.value as AppraisalStatus }))}
              >
                {(['Not Started', 'In Progress', 'Completed'] as AppraisalStatus[]).map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="block text-xs font-semibold text-stone-700">
            Access Status
            <select
              className={inp}
              value={vehForm.accessStatus}
              onChange={(e) => setVehForm((f) => ({ ...f, accessStatus: e.target.value as AccessStatus }))}
            >
              {(['Has access', 'No access', 'Unknown'] as AccessStatus[]).map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-xs font-semibold text-stone-700">
            Intended Action
            <select
              className={inp}
              value={vehForm.intendedAction}
              onChange={(e) => setVehForm((f) => ({ ...f, intendedAction: e.target.value as IntendedAction }))}
            >
              {(['Retain', 'Transfer', 'Sell', 'Unknown'] as IntendedAction[]).map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-xs font-semibold text-stone-700">
            Executor Progress Status
            <select
              className={inp}
              value={vehForm.executorProgressStatus}
              onChange={(e) => setVehForm((f) => ({ ...f, executorProgressStatus: e.target.value as ExecutorProgress }))}
            >
              {(['Not Started', 'In Progress', 'Completed', 'Blocked'] as ExecutorProgress[]).map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-xs font-semibold text-stone-700">
            Notes
            <textarea
              className={`${inp} min-h-[72px]`}
              value={vehForm.notes}
              onChange={(e) => setVehForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </label>

          <div>
            <p className="text-xs font-semibold text-stone-600 mb-2">Attachments (saved on Save)</p>
            <input
              type="file"
              multiple
              disabled={isArchived}
              className="text-sm"
              onChange={(e) => setVehFiles(Array.from(e.target.files || []))}
            />
            {vehFiles.length > 0 && <p className="text-xs text-stone-500 mt-1">{vehFiles.length} file(s) selected</p>}
          </div>
        </div>
      </ModalWrapper>
    </SettlementStepPage>
  );
}